import dayjs from 'dayjs'

/**
 * Identifies the QUESTION a saved report answers ("fit for how long?"), so a
 * unit-wide verdict only ever aggregates like-for-like.
 *
 * Its own module because the key is a value object in its own right: report
 * parsing produces one, the rollup groups by one, and the close-out selector
 * renders one — none of which should have to depend on the others.
 *
 * Two shapes, in priority order:
 *   `date:<YYYY-MM-DD>` — from `user_params.targetDate`
 *   `label:<text>`      — from the free-text `outcome.scenario`
 */
export type ScenarioKey = string

const DATE_KEY_PREFIX = 'date:'
const LABEL_KEY_PREFIX = 'label:'
const ISO_DATE_PREFIX = /^(\d{4}-\d{2}-\d{2})/

/**
 * Extract the calendar date WITHOUT going through a timezone.
 *
 * Deliberately not `dayjs(value).format('YYYY-MM-DD')`: that renders in the
 * viewer's local zone, so `2031-01-01T00:00:00.000Z` becomes `2030-12-31` for
 * anyone west of UTC. This key groups reports across a whole unit, so a
 * viewer-dependent key would split one scenario in two for some engineers and
 * not others — precisely what canonicalising is for. Taking the literal date
 * prefix is timezone-proof and covers both the stored `YYYY-MM-DD` form and a
 * full ISO timestamp.
 */
function toCalendarDate(value: string | null): string | null {
  if (!value) return null
  const match = ISO_DATE_PREFIX.exec(value)
  if (!match) return null
  const candidate = match[1]
  // `isValid()` alone is too lenient -- dayjs rolls `2031-13-45` over into a
  // real date rather than rejecting it. Round-tripping catches that, and is
  // timezone-safe because a bare date parses as LOCAL midnight and formats back
  // in the same zone, so nothing shifts.
  const parsed = dayjs(candidate)
  return parsed.isValid() && parsed.format('YYYY-MM-DD') === candidate
    ? candidate
    : null
}

/**
 * Canonicalise a report's scenario identity.
 *
 * `targetDate` wins because it is STRUCTURED and load-bearing: it drives the
 * future corrosion allowance via
 * `fca = corrosionRate × (targetDate − inspectionDate)`
 * (`FfsLevelOneUserInputSection.tsx`), so a real run-length case has one. It is
 * nullable and unenforced, hence the label fallback.
 *
 * The label is never the primary key: it comes from a free-text `mode='tags'`
 * Select, so "5-year" and "5 Year" would silently fragment the scenario space.
 * `null` means the report cannot be grouped at all.
 */
export function scenarioKeyOf(
  targetDate: string | null,
  scenarioLabel: string | null
): ScenarioKey | null {
  const calendarDate = toCalendarDate(targetDate)
  if (calendarDate) return `${DATE_KEY_PREFIX}${calendarDate}`
  const trimmed = scenarioLabel?.trim()
  if (trimmed) return `${LABEL_KEY_PREFIX}${trimmed}`
  return null
}

/** Inverse of `scenarioKeyOf`, for rendering and for the persisted payload. */
export function describeScenario(key: ScenarioKey): {
  targetDate: string | null
  label: string | null
} {
  if (key.startsWith(DATE_KEY_PREFIX)) {
    return { targetDate: key.slice(DATE_KEY_PREFIX.length), label: null }
  }
  if (key.startsWith(LABEL_KEY_PREFIX)) {
    return { targetDate: null, label: key.slice(LABEL_KEY_PREFIX.length) }
  }
  return { targetDate: null, label: null }
}

/** Human-facing name for the scenario selector. */
export function scenarioDisplayLabel(key: ScenarioKey): string {
  const { targetDate, label } = describeScenario(key)
  return targetDate ? `Target ${targetDate}` : (label ?? 'Unnamed scenario')
}
